import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { constructAlternativeOffersKey, fetchPeopleLikeMeRecommendations, fetchPersonalisedAlternativeOffers, fetchPersonalisedAlternativeOffersWithOfferEnquires, personalisedAlternativesOfferType } from 'actions/RecommendationActions'

import { useAppDispatch } from 'hooks/reduxHooks'
import Heading from 'components/Luxkit/Typography/Heading'
import { take, without } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferCarouselHeader from 'home/components/OfferCarousels/common/OfferCarouselHeader'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import TextLink from 'components/Luxkit/TextLink'
import { AlternativeOfferEnquiry } from 'api/recommendations'
import useOfferList from 'hooks/Offers/useOfferList'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { ALL_HOTEL_TYPES, LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import { isLoggedIn } from 'selectors/accountSelectors'
import { useInView } from 'react-intersection-observer'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

const Container = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.seven};
  padding: ${rem(20)};
  width: ${rem(320)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)};
  }

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(60)};
  }

`

interface Props {
  userGivenName?: string;
  region: string;
  isLoggedIn: boolean;
  highIntent: App.RecommendationState['highIntent'];
  alternativesByUser: App.RecommendationState['personalisedAlternatives'];
  alternativesByOffers: App.RecommendationState['personalisedAlternativesWithOfferEnquiries'];
  peopleLikeMe: App.RecommendationState['personalisedPeopleLikeMe'];
}

/**
 * Top pick for me carousel
 *
 * The offers are selected based on (fallback to next if no recos)
 * 1. personalised alternative
 * 2. people like me
 */
function TopPicksForMeCarousel({
  userGivenName,
  region,
  isLoggedIn,
  highIntent,
  alternativesByUser,
  alternativesByOffers,
  peopleLikeMe,
}: Props) {
  const dispatch = useAppDispatch()

  // when not login users in AU viewed this carousel, will trigger the experiment
  const { ref, inView } = useInView({ triggerOnce: true })
  useOptimizelyExperiment(OptimizelyServerSideExperiments.lereHomepageTopPickCarousel, !isLoggedIn && region === 'AU' && inView)

  const offerEnquiry: AlternativeOfferEnquiry[] = useMemo(
    () => highIntent.offers
      .filter(offer => personalisedAlternativesOfferType.includes(offer.offerType)) // Filter out offers with categories which are not support for LERE
      .map((offer) => ({
        offer_id: offer.offerId,
        ...('checkIn' in offer && !!offer.checkIn && { check_in: offer.checkIn as string }), // Conditionally include check_in
        ...('checkOut' in offer && !!offer.checkOut && { check_out: offer.checkOut as string }), // Conditionally include check_out
      })),
    [highIntent],
  )

  const alternativeByOffersKey: string = useMemo(
    () => constructAlternativeOffersKey(offerEnquiry), [offerEnquiry],
  )

  const highIntentOfferIds: string[] = useMemo(
    () => highIntent.offers.map(offer => offer.offerId),
    [highIntent],
  )

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchPersonalisedAlternativeOffers())
      dispatch(fetchPeopleLikeMeRecommendations())
    } else if (offerEnquiry.length > 0) {
      dispatch(fetchPersonalisedAlternativeOffersWithOfferEnquires(offerEnquiry))
    }
  }, [dispatch, offerEnquiry, region, isLoggedIn])

  // --------- Here is the AB test for the fall back to hotel list offer -------------------
  const isManualSortingEnabled = isManualSortingEnabledFor('carousel-hotel')
  const sortBy = 'value'

  const offerListFilters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled) {
      return {
        offerTypes: ALL_HOTEL_TYPES,
        sortBy: 'carousel-hotel',
        limit: MAX_CAROUSEL_ITEM_COUNT,
      }
    } else {
      return {
        offerTypes: LE_CONTRACTED_HOTEL_TYPES,
        frontPageVisibleOnly: true,
        limit: MAX_CAROUSEL_ITEM_COUNT,
        sortBy,
      }
    }
  }, [isManualSortingEnabled])

  const fallbackOfferList = useOfferList(offerListFilters)
  // --------- End the AB test for the fall back to hotel list offer ---------------------

  const recommendationsLoading = useMemo(() => {
    let alternativeLoading : boolean = true
    let recommendationsLoading : boolean = true

    if (isLoggedIn) {
      alternativeLoading = alternativesByUser.state === 'loading'
      const plmLoading = peopleLikeMe.state === 'loading'
      recommendationsLoading = alternativeLoading || (alternativesByUser.offers.length < 10 && plmLoading)
    } else {
      const localHighIntentDone = highIntent.lereVersion === 'local' && highIntent.state === 'done'
      // if they don't have local storage high intent offers, set the recommendationsLoading to false in order to fall back to hotel offers
      recommendationsLoading = !(localHighIntentDone && offerEnquiry.length === 0)
      // if they have local storage high intent offers, waiting for the alternative recommendations
      if (localHighIntentDone && offerEnquiry.length > 0 && alternativesByOffers[alternativeByOffersKey]) {
        recommendationsLoading = alternativesByOffers[alternativeByOffersKey].state === 'loading'
      }
    }

    return recommendationsLoading
  }, [alternativeByOffersKey, alternativesByOffers, alternativesByUser, highIntent.lereVersion, highIntent.state, offerEnquiry.length, peopleLikeMe.state, isLoggedIn])

  const recommendedOfferIds = useMemo(() => {
    let candidates: string[] = []

    if (!recommendationsLoading) {
      if (isLoggedIn) {
        // if login, use alt (by user id) first, then follow by the plm as fallback
        const candidatesList = [...alternativesByUser.offers, ...peopleLikeMe.offers]
        candidates = candidatesList.map(offer => offer.offerId)
      } else {
        // if not login, use alt (by offer enquiries) first, then front hotel offers as fallback
        const candidatesIds = alternativesByOffers[alternativeByOffersKey] ? alternativesByOffers[alternativeByOffersKey].offers.map(offer => offer.offerId) : []
        candidates = [...candidatesIds, ...fallbackOfferList.offerIds]
      }
    }
    const dedupCandidates = without(candidates, ...highIntentOfferIds)
    return take(dedupCandidates, MAX_CAROUSEL_ITEM_COUNT)
  }, [recommendationsLoading, highIntentOfferIds, isLoggedIn, alternativesByUser.offers, peopleLikeMe.offers, fallbackOfferList.offerIds, alternativesByOffers, alternativeByOffersKey])

  const offerList = useMemo((): App.OfferList => ({
    fetching: recommendationsLoading,
    error: undefined,
    offerIds: recommendedOfferIds,
    key: 'topPicks',
  }), [recommendationsLoading, recommendedOfferIds])

  if (!recommendationsLoading && recommendedOfferIds.length === 0) {
    return null
  }

  return (
    <SectionContainer gap={20}>
      <LayoutContainer ref={ref}>
        <OfferCarouselHeader allTo={userGivenName ? '/top-picks-for-me' : ''}>
          Top picks for <i>{userGivenName || 'you'}</i>
        </OfferCarouselHeader>
        <TextLink to="/account/travel-preferences" size="medium" weight="regular">
          Edit your preferences
        </TextLink>
      </LayoutContainer>
      <OfferListCarousel
        overrideOfferList={offerList}
        mobileLimit={8}
      >
        {!!userGivenName && <Container direction="vertical" horizontalAlign="center" verticalAlign="center" gap={24}>
          <VerticalSpacer gap={8}>
            <Heading align="center" variant="heading5">Looking for something else?</Heading>
            <BodyText align="center" variant="medium">Discover amazing curated offers tailored to you.</BodyText>
          </VerticalSpacer>
          <TextButton kind="primary" size="medium" shape="square" to="/top-picks-for-me">
            View all
          </TextButton>
        </Container>}
      </OfferListCarousel>
    </SectionContainer>
  )
}

function mapState(state: App.State) {
  return {
    userGivenName: state.auth.account.givenName,
    region: state.geo.currentRegionCode,
    isLoggedIn: isLoggedIn(state),
    highIntent: state.recommendations.highIntent,
    alternativesByUser: state.recommendations.personalisedAlternatives,
    alternativesByOffers: state.recommendations.personalisedAlternativesWithOfferEnquiries,
    peopleLikeMe: state.recommendations.personalisedPeopleLikeMe,
  }
}

export default connect(mapState)(TopPicksForMeCarousel)
